import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

export default function FeeManagement() {
  const navigate = useNavigate();
  const [fees, setFees] = useState([]);
  const [tiers, setTiers] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  
  const [showAddFee, setShowAddFee] = useState(false);
  const [showEditFee, setShowEditFee] = useState(false);
  const [showAddTier, setShowAddTier] = useState(false);
  const [selectedFee, setSelectedFee] = useState(null);
  
  const [feeForm, setFeeForm] = useState({
    transactionType: "",
    feeType: "Flat",
    flatAmount: 0,
    percentage: 0,
    minimumFee: 0,
    maximumFee: 0,
    minTransactionAmount: 0,
    maxTransactionAmount: 999999999,
    chargedTo: "Sender",
    description: "",
    isActive: true
  });
  
  const [tierForm, setTierForm] = useState({
    fromAmount: 0,
    toAmount: 0,
    flatFee: 0,
    percentageFee: 0,
    isActive: true
  });

  const token = localStorage.getItem("token");

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    if (!user.isAdmin) {
      navigate("/dashboard");
      return;
    }
    fetchFees();
  }, [navigate]);

  const fetchFees = async () => {
    try {
      setLoading(true);
      const response = await fetch("/api/Fee", {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      setFees(data);
      
      for (const fee of data) {
        if (fee.feeType === "Tiered") {
          await fetchTiers(fee.id);
        }
      }
    } catch (err) {
      setError("Failed to load fees");
    } finally {
      setLoading(false);
    }
  };

  const fetchTiers = async (feeId) => {
    try {
      const response = await fetch(`/api/Fee/${feeId}/tiers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      setTiers(prev => ({ ...prev, [feeId]: data }));
    } catch (err) {
      console.error("Failed to load tiers");
    }
  };

  const handleCreateFee = async (e) => {
    e.preventDefault();
    setError(""); setSuccess("");
    try {
      const response = await fetch("/api/Fee", {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify(feeForm)
      });
      if (!response.ok) throw new Error((await response.json()).message || "Failed");
      setSuccess("Fee created!"); setShowAddFee(false); resetFeeForm(); fetchFees();
    } catch (err) { setError(err.message); }
  };

  const handleUpdateFee = async (e) => {
    e.preventDefault();
    setError(""); setSuccess("");
    try {
      const response = await fetch(`/api/Fee/${selectedFee.id}`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ ...feeForm, id: selectedFee.id })
      });
      if (!response.ok) throw new Error((await response.json()).message || "Failed");
      setSuccess("Fee updated!"); setShowEditFee(false); setSelectedFee(null); resetFeeForm(); fetchFees();
    } catch (err) { setError(err.message); }
  };

  const handleDeleteFee = async (feeId) => {
    if (!window.confirm("Delete this fee?")) return;
    try {
      await fetch(`/api/Fee/${feeId}`, {
        method: "DELETE", headers: { Authorization: `Bearer ${token}` }
      });
      setSuccess("Fee deleted!"); fetchFees();
    } catch (err) { setError(err.message); }
  };

  const handleToggleStatus = async (fee) => {
    try {
      await fetch(`/api/Fee/${fee.id}`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ ...fee, isActive: !fee.isActive })
      });
      setSuccess(`Fee ${fee.isActive ? "disabled" : "enabled"}!`); fetchFees();
    } catch (err) { setError(err.message); }
  };

  const handleCreateTier = async (e) => {
    e.preventDefault();
    try {
      await fetch(`/api/Fee/${selectedFee.id}/tiers`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ ...tierForm, transactionFeeId: selectedFee.id })
      });
      setSuccess("Tier added!"); setShowAddTier(false); resetTierForm(); fetchTiers(selectedFee.id);
    } catch (err) { setError(err.message); }
  };

  const handleDeleteTier = async (tierId, feeId) => {
    if (!window.confirm("Delete this tier?")) return;
    try {
      await fetch(`/api/Fee/tiers/${tierId}`, {
        method: "DELETE", headers: { Authorization: `Bearer ${token}` }
      });
      setSuccess("Tier deleted!"); fetchTiers(feeId);
    } catch (err) { setError(err.message); }
  };

  const openEditModal = (fee) => {
    setSelectedFee(fee);
    setFeeForm({
      transactionType: fee.transactionType, feeType: fee.feeType,
      flatAmount: fee.flatAmount || 0, percentage: fee.percentage || 0,
      minimumFee: fee.minimumFee || 0, maximumFee: fee.maximumFee || 0,
      minTransactionAmount: fee.minTransactionAmount || 0,
      maxTransactionAmount: fee.maxTransactionAmount || 999999999,
      chargedTo: fee.chargedTo || "Sender", description: fee.description || "",
      isActive: fee.isActive
    });
    setShowEditFee(true);
  };

  const resetFeeForm = () => setFeeForm({
    transactionType: "", feeType: "Flat", flatAmount: 0, percentage: 0,
    minimumFee: 0, maximumFee: 0, minTransactionAmount: 0,
    maxTransactionAmount: 999999999, chargedTo: "Sender", description: "", isActive: true
  });

  const resetTierForm = () => setTierForm({ fromAmount: 0, toAmount: 0, flatFee: 0, percentageFee: 0, isActive: true });

  const fmt = (n) => `₦${Number(n).toLocaleString()}`;

  if (loading) return <div style={{textAlign:"center",marginTop:"100px"}}>Loading...</div>;

  return (
    <div style={{maxWidth:"1200px",margin:"0 auto",padding:"20px"}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:"20px",flexWrap:"wrap",gap:"10px"}}>
        <h1>💰 Fee Management</h1>
        <div style={{display:"flex",gap:"10px"}}>
          <button onClick={() => navigate("/admin")} style={{padding:"10px 20px",backgroundColor:"#6c757d",color:"white",border:"none",borderRadius:"6px",cursor:"pointer"}}>← Back</button>
          <button onClick={() => {resetFeeForm();setShowAddFee(true);}} style={{padding:"10px 20px",backgroundColor:"#28a745",color:"white",border:"none",borderRadius:"6px",cursor:"pointer"}}>➕ Add Fee</button>
        </div>
      </div>

      {error && <div style={{backgroundColor:"#fee",color:"#c33",padding:"15px",borderRadius:"6px",marginBottom:"20px"}}>{error}</div>}
      {success && <div style={{backgroundColor:"#d4edda",color:"#155724",padding:"15px",borderRadius:"6px",marginBottom:"20px"}}>{success}</div>}

      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(350px,1fr))",gap:"20px"}}>
        {fees.map(fee => (
          <div key={fee.id} style={{backgroundColor:"white",borderRadius:"8px",boxShadow:"0 2px 8px rgba(0,0,0,0.1)",padding:"20px",borderLeft:`4px solid ${fee.isActive?"#28a745":"#dc3545"}`}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:"15px"}}>
              <h3 style={{margin:0}}>{fee.transactionType}</h3>
              <span style={{padding:"4px 12px",borderRadius:"12px",fontSize:"12px",color:"white",backgroundColor:fee.isActive?"#28a745":"#dc3545"}}>{fee.isActive?"Active":"Inactive"}</span>
            </div>
            
            <div style={{marginBottom:"15px"}}>
              <div style={{display:"flex",justifyContent:"space-between",padding:"8px 0",borderBottom:"1px solid #eee"}}><span>Type:</span><strong>{fee.feeType}</strong></div>
              {fee.feeType === "Flat" && <div style={{display:"flex",justifyContent:"space-between",padding:"8px 0",borderBottom:"1px solid #eee"}}><span>Amount:</span><strong>{fmt(fee.flatAmount)}</strong></div>}
              {fee.feeType === "Percentage" && (
                <>
                  <div style={{display:"flex",justifyContent:"space-between",padding:"8px 0",borderBottom:"1px solid #eee"}}><span>Rate:</span><strong>{fee.percentage}%</strong></div>
                  {fee.minimumFee > 0 && <div style={{display:"flex",justifyContent:"space-between",padding:"8px 0",borderBottom:"1px solid #eee"}}><span>Min:</span><strong>{fmt(fee.minimumFee)}</strong></div>}
                  {fee.maximumFee > 0 && <div style={{display:"flex",justifyContent:"space-between",padding:"8px 0",borderBottom:"1px solid #eee"}}><span>Max:</span><strong>{fmt(fee.maximumFee)}</strong></div>}
                </>
              )}
              <div style={{display:"flex",justifyContent:"space-between",padding:"8px 0"}}><span>Charged To:</span><strong>{fee.chargedTo}</strong></div>
              {fee.description && <div style={{marginTop:"10px",padding:"10px",backgroundColor:"#f8f9fa",borderRadius:"4px",fontSize:"13px"}}>{fee.description}</div>}
            </div>

            {fee.feeType === "Tiered" && (
              <div style={{marginTop:"15px",padding:"15px",backgroundColor:"#f8f9fa",borderRadius:"6px"}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:"10px"}}>
                  <h4 style={{margin:0}}>Tiers</h4>
                  <button onClick={() => {setSelectedFee(fee);resetTierForm();setShowAddTier(true);}} style={{padding:"5px 10px",fontSize:"12px",backgroundColor:"#007bff",color:"white",border:"none",borderRadius:"4px",cursor:"pointer"}}>+ Add</button>
                </div>
                {tiers[fee.id]?.length > 0 ? (
                  <table style={{width:"100%",borderCollapse:"collapse",fontSize:"13px"}}>
                    <thead><tr><th>From</th><th>To</th><th>Fee</th><th></th></tr></thead>
                    <tbody>
                      {tiers[fee.id].map(t => (
                        <tr key={t.id}>
                          <td>{fmt(t.fromAmount)}</td>
                          <td>{fmt(t.toAmount)}</td>
                          <td>{t.flatFee > 0 ? fmt(t.flatFee) : `${t.percentageFee}%`}</td>
                          <td><button onClick={() => handleDeleteTier(t.id, fee.id)} style={{background:"none",border:"none",cursor:"pointer"}}>🗑️</button></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : <p style={{textAlign:"center",color:"#666",fontSize:"13px"}}>No tiers</p>}
              </div>
            )}

            <div style={{display:"flex",gap:"10px",marginTop:"15px",paddingTop:"15px",borderTop:"1px solid #eee"}}>
              <button onClick={() => openEditModal(fee)} style={{flex:1,padding:"8px",backgroundColor:"#007bff",color:"white",border:"none",borderRadius:"4px",cursor:"pointer",fontSize:"13px"}}>✏️ Edit</button>
              <button onClick={() => handleToggleStatus(fee)} style={{flex:1,padding:"8px",backgroundColor:fee.isActive?"#ffc107":"#28a745",color:fee.isActive?"#333":"white",border:"none",borderRadius:"4px",cursor:"pointer",fontSize:"13px"}}>{fee.isActive?"🚫 Disable":"✅ Enable"}</button>
              <button onClick={() => handleDeleteFee(fee.id)} style={{flex:1,padding:"8px",backgroundColor:"#dc3545",color:"white",border:"none",borderRadius:"4px",cursor:"pointer",fontSize:"13px"}}>🗑️ Delete</button>
            </div>
          </div>
        ))}
      </div>

      {/* Add Fee Modal */}
      {showAddFee && (
        <div style={{position:"fixed",top:0,left:0,width:"100%",height:"100%",backgroundColor:"rgba(0,0,0,0.5)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:1000}}>
          <div style={{backgroundColor:"white",padding:"30px",borderRadius:"8px",width:"90%",maxWidth:"500px",maxHeight:"90vh",overflow:"auto"}}>
            <h2>Add New Fee</h2>
            <form onSubmit={handleCreateFee}>
              <div style={{marginBottom:"15px"}}>
                <label>Transaction Type *</label>
                <input type="text" value={feeForm.transactionType} onChange={e => setFeeForm({...feeForm,transactionType:e.target.value})} required style={{width:"100%",padding:"10px",border:"1px solid #ddd",borderRadius:"4px",marginTop:"5px"}} placeholder="e.g., Transfer, AgentDeposit"/>
              </div>
              <div style={{marginBottom:"15px"}}>
                <label>Fee Type *</label>
                <select value={feeForm.feeType} onChange={e => setFeeForm({...feeForm,feeType:e.target.value})} style={{width:"100%",padding:"10px",border:"1px solid #ddd",borderRadius:"4px",marginTop:"5px"}}>
                  <option value="Flat">Flat Amount</option>
                  <option value="Percentage">Percentage</option>
                  <option value="Tiered">Tiered</option>
                </select>
              </div>
              {feeForm.feeType === "Flat" && (
                <div style={{marginBottom:"15px"}}>
                  <label>Flat Amount (₦)</label>
                  <input type="number" value={feeForm.flatAmount} onChange={e => setFeeForm({...feeForm,flatAmount:parseFloat(e.target.value)||0})} min="0" step="0.01" style={{width:"100%",padding:"10px",border:"1px solid #ddd",borderRadius:"4px",marginTop:"5px"}}/>
                </div>
              )}
              {feeForm.feeType === "Percentage" && (
                <>
                  <div style={{marginBottom:"15px"}}>
                    <label>Percentage (%)</label>
                    <input type="number" value={feeForm.percentage} onChange={e => setFeeForm({...feeForm,percentage:parseFloat(e.target.value)||0})} min="0" max="100" step="0.01" style={{width:"100%",padding:"10px",border:"1px solid #ddd",borderRadius:"4px",marginTop:"5px"}}/>
                  </div>
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"10px",marginBottom:"15px"}}>
                    <div><label>Min Fee (₦)</label><input type="number" value={feeForm.minimumFee} onChange={e => setFeeForm({...feeForm,minimumFee:parseFloat(e.target.value)||0})} min="0" style={{width:"100%",padding:"10px",border:"1px solid #ddd",borderRadius:"4px",marginTop:"5px"}}/></div>
                    <div><label>Max Fee (₦)</label><input type="number" value={feeForm.maximumFee} onChange={e => setFeeForm({...feeForm,maximumFee:parseFloat(e.target.value)||0})} min="0" style={{width:"100%",padding:"10px",border:"1px solid #ddd",borderRadius:"4px",marginTop:"5px"}}/></div>
                  </div>
                </>
              )}
              <div style={{marginBottom:"15px"}}>
                <label>Charged To</label>
                <select value={feeForm.chargedTo} onChange={e => setFeeForm({...feeForm,chargedTo:e.target.value})} style={{width:"100%",padding:"10px",border:"1px solid #ddd",borderRadius:"4px",marginTop:"5px"}}>
                  <option value="Sender">Sender</option>
                  <option value="Receiver">Receiver</option>
                </select>
              </div>
              <div style={{marginBottom:"15px"}}>
                <label>Description</label>
                <textarea value={feeForm.description} onChange={e => setFeeForm({...feeForm,description:e.target.value})} style={{width:"100%",padding:"10px",border:"1px solid #ddd",borderRadius:"4px",marginTop:"5px",minHeight:"60px"}}/>
              </div>
              <div style={{display:"flex",gap:"10px",justifyContent:"flex-end"}}>
                <button type="submit" style={{padding:"10px 25px",backgroundColor:"#28a745",color:"white",border:"none",borderRadius:"6px",cursor:"pointer"}}>Create</button>
                <button type="button" onClick={() => setShowAddFee(false)} style={{padding:"10px 25px",backgroundColor:"white",color:"#333",border:"1px solid #ddd",borderRadius:"6px",cursor:"pointer"}}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Fee Modal */}
      {showEditFee && selectedFee && (
        <div style={{position:"fixed",top:0,left:0,width:"100%",height:"100%",backgroundColor:"rgba(0,0,0,0.5)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:1000}}>
          <div style={{backgroundColor:"white",padding:"30px",borderRadius:"8px",width:"90%",maxWidth:"500px",maxHeight:"90vh",overflow:"auto"}}>
            <h2>Edit: {selectedFee.transactionType}</h2>
            <form onSubmit={handleUpdateFee}>
              <div style={{marginBottom:"15px"}}>
                <label>Transaction Type</label>
                <input type="text" value={feeForm.transactionType} onChange={e => setFeeForm({...feeForm,transactionType:e.target.value})} required style={{width:"100%",padding:"10px",border:"1px solid #ddd",borderRadius:"4px",marginTop:"5px"}}/>
              </div>
              <div style={{marginBottom:"15px"}}>
                <label>Fee Type</label>
                <select value={feeForm.feeType} onChange={e => setFeeForm({...feeForm,feeType:e.target.value})} style={{width:"100%",padding:"10px",border:"1px solid #ddd",borderRadius:"4px",marginTop:"5px"}}>
                  <option value="Flat">Flat</option>
                  <option value="Percentage">Percentage</option>
                  <option value="Tiered">Tiered</option>
                </select>
              </div>
              {feeForm.feeType === "Flat" && (
                <div style={{marginBottom:"15px"}}>
                  <label>Flat Amount (₦)</label>
                  <input type="number" value={feeForm.flatAmount} onChange={e => setFeeForm({...feeForm,flatAmount:parseFloat(e.target.value)||0})} min="0" style={{width:"100%",padding:"10px",border:"1px solid #ddd",borderRadius:"4px",marginTop:"5px"}}/>
                </div>
              )}
              {feeForm.feeType === "Percentage" && (
                <>
                  <div style={{marginBottom:"15px"}}>
                    <label>Percentage (%)</label>
                    <input type="number" value={feeForm.percentage} onChange={e => setFeeForm({...feeForm,percentage:parseFloat(e.target.value)||0})} min="0" max="100" step="0.01" style={{width:"100%",padding:"10px",border:"1px solid #ddd",borderRadius:"4px",marginTop:"5px"}}/>
                  </div>
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"10px",marginBottom:"15px"}}>
                    <div><label>Min Fee</label><input type="number" value={feeForm.minimumFee} onChange={e => setFeeForm({...feeForm,minimumFee:parseFloat(e.target.value)||0})} min="0" style={{width:"100%",padding:"10px",border:"1px solid #ddd",borderRadius:"4px",marginTop:"5px"}}/></div>
                    <div><label>Max Fee</label><input type="number" value={feeForm.maximumFee} onChange={e => setFeeForm({...feeForm,maximumFee:parseFloat(e.target.value)||0})} min="0" style={{width:"100%",padding:"10px",border:"1px solid #ddd",borderRadius:"4px",marginTop:"5px"}}/></div>
                  </div>
                </>
              )}
              <div style={{marginBottom:"15px"}}>
                <label>Charged To</label>
                <select value={feeForm.chargedTo} onChange={e => setFeeForm({...feeForm,chargedTo:e.target.value})} style={{width:"100%",padding:"10px",border:"1px solid #ddd",borderRadius:"4px",marginTop:"5px"}}>
                  <option value="Sender">Sender</option>
                  <option value="Receiver">Receiver</option>
                </select>
              </div>
              <div style={{marginBottom:"15px"}}>
                <label>Description</label>
                <textarea value={feeForm.description} onChange={e => setFeeForm({...feeForm,description:e.target.value})} style={{width:"100%",padding:"10px",border:"1px solid #ddd",borderRadius:"4px",marginTop:"5px",minHeight:"60px"}}/>
              </div>
              <div style={{marginBottom:"15px"}}>
                <label><input type="checkbox" checked={feeForm.isActive} onChange={e => setFeeForm({...feeForm,isActive:e.target.checked})}/> Active</label>
              </div>
              <div style={{display:"flex",gap:"10px",justifyContent:"flex-end"}}>
                <button type="submit" style={{padding:"10px 25px",backgroundColor:"#007bff",color:"white",border:"none",borderRadius:"6px",cursor:"pointer"}}>Update</button>
                <button type="button" onClick={() => {setShowEditFee(false);setSelectedFee(null);}} style={{padding:"10px 25px",backgroundColor:"white",color:"#333",border:"1px solid #ddd",borderRadius:"6px",cursor:"pointer"}}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add Tier Modal */}
      {showAddTier && selectedFee && (
        <div style={{position:"fixed",top:0,left:0,width:"100%",height:"100%",backgroundColor:"rgba(0,0,0,0.5)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:1000}}>
          <div style={{backgroundColor:"white",padding:"30px",borderRadius:"8px",width:"90%",maxWidth:"400px"}}>
            <h2>Add Tier: {selectedFee.transactionType}</h2>
            <form onSubmit={handleCreateTier}>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"10px",marginBottom:"15px"}}>
                <div><label>From (₦)</label><input type="number" value={tierForm.fromAmount} onChange={e => setTierForm({...tierForm,fromAmount:parseFloat(e.target.value)||0})} min="0" required style={{width:"100%",padding:"10px",border:"1px solid #ddd",borderRadius:"4px",marginTop:"5px"}}/></div>
                <div><label>To (₦)</label><input type="number" value={tierForm.toAmount} onChange={e => setTierForm({...tierForm,toAmount:parseFloat(e.target.value)||0})} min="0" required style={{width:"100%",padding:"10px",border:"1px solid #ddd",borderRadius:"4px",marginTop:"5px"}}/></div>
              </div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"10px",marginBottom:"15px"}}>
                <div><label>Flat Fee (₦)</label><input type="number" value={tierForm.flatFee} onChange={e => setTierForm({...tierForm,flatFee:parseFloat(e.target.value)||0})} min="0" style={{width:"100%",padding:"10px",border:"1px solid #ddd",borderRadius:"4px",marginTop:"5px"}}/></div>
                <div><label>OR % Fee</label><input type="number" value={tierForm.percentageFee} onChange={e => setTierForm({...tierForm,percentageFee:parseFloat(e.target.value)||0})} min="0" max="100" step="0.01" style={{width:"100%",padding:"10px",border:"1px solid #ddd",borderRadius:"4px",marginTop:"5px"}}/></div>
              </div>
              <p style={{fontSize:"12px",color:"#666",marginBottom:"15px"}}>💡 Set Flat Fee OR Percentage (Flat takes priority)</p>
              <div style={{display:"flex",gap:"10px",justifyContent:"flex-end"}}>
                <button type="submit" style={{padding:"10px 25px",backgroundColor:"#28a745",color:"white",border:"none",borderRadius:"6px",cursor:"pointer"}}>Add</button>
                <button type="button" onClick={() => {setShowAddTier(false);setSelectedFee(null);}} style={{padding:"10px 25px",backgroundColor:"white",color:"#333",border:"1px solid #ddd",borderRadius:"6px",cursor:"pointer"}}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

